import { useBehavior, useTriggerAction, useUpdateWidgetValue } from '@libs/hooks';
import { ActionActionTypeEnum, TCommonOption, TRowComponentProps, TWidget } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, ChangeEvent, MouseEvent } from 'react';
import { TConsumerItem, TSelectorRegularData } from '../../../types/data';
import { TRowOption, TSelectorRegularOption } from '../../../types/options';
import { getInlineStyles } from '../../../utils/styles';
import { TextCommon } from '../Text/TextCommon';
import { TRowComponent } from './RowComponent';
import { SelectorRegularLeftIcon } from './SelectorRegularLeftIcon';
import { SelectorRegularRight } from './SelectorRegularRight';
import styles from './styles/rowRegular.module.css';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';

export type TRowRegular = {
    uid?: TWidget<TCommonOption>['uid'];
} & Omit<TRowComponentProps, 'id' | 'value'> &
    Omit<TConsumerItem, 'id' | 'value'> &
    Pick<TCommonOption, 'margin'> &
    Pick<TRowComponent, 'handleClick'>;

type TSelectorRegularProps<TD, TO> =
    | {
          isWidget: true;
          widget: TWidget<TD, TO>;
      }
    | ({
          isWidget: false;
          value?: boolean;
      } & TRowRegular &
          Pick<TRowComponent, 'handleClick'>);

export function SelectorRegular(props: TSelectorRegularProps<TSelectorRegularData, TSelectorRegularOption>) {
    const { isWidget } = props;
    const finalWidget = !isWidget
        ? ({
              uid: props.uid ?? '',
              data: {
                  value: props.value,
                  messages: {
                      title: props.title,
                      subtitle: props.subtitle,
                      metaText: props.metaText,
                  },
              },
              options: {
                  isDisabled: props.isDisabled ?? false,
                  isHide: props.isHide ?? false,
                  metaVariant: props.metaVariant,
                  metaHierarchy: props.metaHierarchy,
                  iconName: props.iconName,
                  flagIcon: props.flagIcon,
                  avatar: props.avatar,
                  iconHierarchy: props.iconHierarchy,
                  iconVariant: props.iconVariant,
                  margin: props.margin,
              },
          } as TWidget<TSelectorRegularData, TRowOption & TSelectorRegularOption>)
        : (props.widget as TWidget<TSelectorRegularData, TRowOption & TSelectorRegularOption>);

    const { updateWidgetValue } = useUpdateWidgetValue(finalWidget);
    const { triggerActionsByType } = useTriggerAction(finalWidget);
    const { isDisabled, isHidden } = useBehavior(finalWidget);

    const handleAction = (e: MouseEvent | ChangeEvent) => {
        if (isWidget) {
            updateWidgetValue(!finalWidget.data.value);
            triggerActionsByType(ActionActionTypeEnum.onClick).then();
        } else {
            props.handleClick(e as never);
        }
    };

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <button
                data-test={finalWidget.uid}
                className={clsx(styles.commonRowRegular, isDisabled && styles.disabled)}
                style={getInlineStyles({ margin: finalWidget.options.margin })}
                onClick={handleAction}
                aria-hidden={finalWidget.options?.isHide}
                aria-label={finalWidget.data?.messages?.title}
                aria-disabled={isDisabled}
                disabled={isDisabled}
            >
                <SelectorRegularLeftIcon
                    variant={finalWidget.options?.variant}
                    iconName={finalWidget.options?.iconName}
                    iconHierarchy={finalWidget.options?.iconHierarchy}
                    iconVariant={finalWidget.options?.iconVariant}
                    avatar={finalWidget.options?.avatar}
                    flagIcon={finalWidget.options?.flagIcon}
                    isDisabled={isDisabled}
                />
                <div className={styles.rowContent}>
                    {finalWidget.data?.messages?.title && (
                        <div className={clsx(styles.highlightedTitle, isDisabled && styles.disabled)}>
                            {parse(DOMPurify.sanitize(finalWidget.data?.messages?.title))}
                        </div>
                    )}
                    {finalWidget.data?.messages?.subtitle && (
                        <TextCommon
                            isWidget={false}
                            content={finalWidget.data?.messages?.subtitle}
                            as={'span'}
                            options={{
                                variant: 'secondary',
                                textColor: isDisabled ? 'grey_40_lc' : 'grey_72_lc',
                                margin: { top: 4 },
                            }}
                        />
                    )}
                </div>
                <SelectorRegularRight
                    uid={`right_selector_regular_${finalWidget.uid}`}
                    handleChange={handleAction}
                    isDisabled={isDisabled}
                    value={finalWidget.data.value}
                    metaText={finalWidget.data?.messages?.metaText}
                    metaVariant={finalWidget.options?.metaVariant}
                    metaHierarchy={finalWidget.options?.metaHierarchy}
                    variant={finalWidget.options?.variant}
                />
            </button>
        </Activity>
    );
}
